'use client';

import { useEffect, useState } from 'react';
import { Check, ChevronRight, Pencil } from 'lucide-react';
import { FormField, isVisible } from '@church/nextjs-ui/website/forms';
import type { FormCampusOption } from '@church/nextjs-ui/website/forms';
import type {
  SheetDress,
  ClientField,
  ClientForm,
  ClientPerson,
  MedicalDraft,
  MedicalItem,
  MedicalType,
} from '@/lib/types';
import {
  emergencyFieldsOf,
  groupedIds,
  medicalFieldOf,
  phoneOk,
  prettyPhone,
  type EmergencyFields,
} from '@/lib/questions';
import {
  choicesForGender,
  formValueFor,
  genderChoices,
  needsGender,
  relationshipFromOption,
  wordFor,
  type LearnedRelationship,
  type RelationChoice,
  firstNameOf,
} from '@/lib/relationships';
import { MedicalQuestion, medicalAnswer } from './Medical';
import { Avatar } from './Avatar';

/**
 * THE QUESTIONS, WITH THE TWO THAT ARE REALLY ONE ASKED AS ONE
 * ============================================================
 *
 * Everything MP asks, in MP's order, except that the emergency contact and
 * the waiver are drawn as single controls — see `lib/questions.ts` for how
 * they are recognised. Everything else is the shared form kit, unchanged.
 */
export function Questions({
  form,
  answers,
  setAnswer,
  campuses,
  person,
  household,
  medicalTypes = [],
  medical,
  setMedical,
  medicalNote,
  setMedicalNote,
  registrantName = 'them',
  aboutName = null,
  aboutGender = null,
  personGender = null,
  onPersonGender,
  onLearn,
  onSaveMedical,
  onRemoveMedical,
  onAddMedical,
  answerDress,
  hideFields,
}: {
  form: ClientForm | null;
  answers: Record<string, string>;
  setAnswer: (id: number, value: string) => void;
  campuses?: FormCampusOption[];
  /** The household member being registered, when it is one. */
  person?: ClientPerson | null;
  /** Everyone else in the household: the one-tap answers. */
  household: ClientPerson[];
  /** MP's medical vocabulary, for the chips. */
  medicalTypes?: MedicalType[];
  /** Rows added here, which ride the draft so they survive leaving the page. */
  medical: MedicalDraft[];
  setMedical: (next: MedicalDraft[]) => void;
  medicalNote: string;
  setMedicalNote: (v: string) => void;
  /** Whose questions these are, for the sentences that name two people. */
  registrantName?: string;
  /**
   * The same name, but only when these answers are about somebody OTHER than
   * the reader. A question a field wrote a second phrasing for is asked about
   * them by name; everything else stays in the second person.
   */
  aboutName?: string | null;
  /** What MP holds about them, so a pronoun can fit. Unknown means they/them. */
  aboutGender?: 'male' | 'female' | null;
  /** Their gender for choosing, even when the reader is answering about themselves. */
  personGender?: 'male' | 'female' | null;
  /** A question had to ask their gender; keep it with their answers. */
  onPersonGender?: (gender: 'male' | 'female') => void;
  /** Something true about a real record, learned on the way past. */
  onLearn?: (fact: LearnedRelationship) => void;
  /**
   * Corrections to rows MP already holds. False back means the write failed
   * and the row on screen goes back to what it was. Absent leaves those rows
   * read-only, which is what a guest with no MP record of their own gets.
   */
  onSaveMedical?: (item: MedicalItem, patch: MedicalDraft) => Promise<boolean>;
  onRemoveMedical?: (item: MedicalItem) => Promise<boolean>;
  onAddMedical?: (draft: MedicalDraft) => Promise<boolean>;
  /** An experience's photo cards for a choice question (lib/types SheetDress). */
  answerDress?: SheetDress['answers'];
  /** Fields the dress replaces. */
  hideFields?: number[];
}) {
  const emergency = emergencyFieldsOf(form);
  const medicalField = medicalFieldOf(form);
  const swallowed = groupedIds(form);

  const top = (form?.fields ?? []).filter((f) => !f.parentId);
  const childrenOf = (id: number) => (form?.fields ?? []).filter((f) => f.parentId === id);
  const shown = top.filter((f) => (form ? isVisible(form, f, answers) : true));

  return (
    <>
      {shown.map((f) => {
        // The two groups are drawn once, where their first field stood, so
        // MP's order still decides where they land on the page.
        if (emergency && f.id === emergency.name.id)
          return (
            <EmergencyContactField
              key={f.id}
              fields={emergency}
              answers={answers}
              setAnswer={setAnswer}
              person={person ?? null}
              household={household}
              form={form}
              registrantName={registrantName}
              onLearn={onLearn}
            />
          );
        if (medicalField && f.id === medicalField.id) {
          const onFile = person?.medical ?? [];
          // One answer in MP, rebuilt from the rows and the note each time
          // either changes: the report the student team runs keeps working.
          const write = (rows: MedicalDraft[], note: string) =>
            setAnswer(f.id, medicalAnswer(onFile, rows, note));
          return (
            <MedicalQuestion
              key={f.id}
              field={f}
              onFile={onFile}
              added={medical}
              setAdded={(rows) => {
                setMedical(rows);
                write(rows, medicalNote);
              }}
              note={medicalNote}
              setNote={(v) => {
                setMedicalNote(v);
                write(medical, v);
              }}
              types={medicalTypes}
              onSaveItem={onSaveMedical}
              onRemoveItem={onRemoveMedical}
              onAddItem={onAddMedical}
            />
          );
        }
        if (swallowed.has(f.id) || hideFields?.includes(f.id)) return null;
        if (answerDress?.[f.id] && f.options?.length)
          return (
            <DressedChoice
              key={f.id}
              label={f.label ?? ''}
              required={f.required}
              choices={f.options}
              dress={answerDress[f.id]!}
              value={answers[String(f.id)] ?? ''}
              onChoose={(v) => setAnswer(f.id, v)}
            />
          );
        return (
          <FormField
            key={f.id}
            field={f}
            children={childrenOf(f.id)}
            answers={answers}
            setAnswer={setAnswer}
            campuses={campuses}
            aboutName={aboutName}
            aboutGender={aboutGender}
            personGender={personGender}
            onPersonGender={onPersonGender}
          />
        );
      })}
    </>
  );
}

/**
 * WHO TO CALL, AS A PERSON — AND WHO THEY ARE TO THIS ONE
 * ======================================================
 *
 * The football and student retreat sites both ask this as one card: the
 * answer on file, a face on it, and a tap to change. Changing it offers the
 * household first, because a household member is a real record — their
 * number stays right when they change it — and typing is the fallback for
 * somebody the church does not know.
 *
 * THE RELATIONSHIP IS NOT A DROPDOWN. It is a sentence about two people, and
 * MP can usually finish it: a `Contact_Relationships` row between them plus
 * the contact's gender is exactly "Sarah is Rosie's mother". So the question
 * is only asked when a fact is actually missing, and only the missing one:
 *
 *   both known      say it, ask nothing
 *   relationship,   "Sarah is Rosie's parent" — mom or dad? (two chips, and
 *   no gender        the answer is a gender we did not have)
 *   neither         "Sarah is Rosie's…" with gendered chips, so one tap
 *                    answers both questions at once
 *
 * And what is learned goes back to the record through `onLearn`, the moment
 * it is tapped — not at the end of the form, which somebody may never reach.
 * The MP form field is still written too: it is what the night's report runs
 * on.
 */
function EmergencyContactField({
  fields,
  answers,
  setAnswer,
  person,
  household,
  form,
  registrantName,
  onLearn,
}: {
  fields: EmergencyFields;
  answers: Record<string, string>;
  setAnswer: (id: number, value: string) => void;
  person: ClientPerson | null;
  household: ClientPerson[];
  form: ClientForm | null;
  /** Whose emergency contact this is, for the sentence. */
  registrantName: string;
  /** A fact worth keeping, on its way to the record. */
  onLearn?: (fact: LearnedRelationship) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [asking, setAsking] = useState(false);
  const [saved, setSaved] = useState(false);
  const name = answers[fields.name.id] ?? '';
  const phone = fields.phone ? (answers[fields.phone.id] ?? '') : '';

  const others = household.filter((p) => p.contactId !== person?.contactId && p.phone);

  /**
   * WHO THIS IS, BY ID WHERE MP SAYS SO.
   *
   * Matching on the spelling of a name alone was wrong in both directions. It
   * failed on MP's own answer, because the record arrives as one string and a
   * household member as two, and it would fail again on a middle name or a
   * Bob who is filed as Robert. Every failure looks the same from here — the
   * pair reads as strangers, so a relationship already on file is never found
   * and the person is asked to declare a wife of eleven years.
   *
   * MP's emergency-contact row already names a contact. Where the name on the
   * form is still the one that came off that row, believe the id. The spelling
   * comparison stays for anything typed in since.
   */
  const fromRecordId =
    person?.emergency?.kind === 'person' &&
    person.emergency.contactId &&
    person.emergency.name.trim() === name.trim()
      ? (others.find((p) => p.contactId === person.emergency?.contactId) ?? null)
      : null;
  // A chosen household member, or the record's own answer, is what wears a
  // face. A typed name we cannot resolve gets initials, not an invented one.
  const match =
    fromRecordId ??
    others.find((p) => `${p.firstName} ${p.lastName}`.trim() === name.trim()) ??
    null;
  const photo = match?.image ?? (person?.emergency?.name === name ? person.emergency.image : null);

  // What MP already knows about the pair, then what this form was answered
  // with, in that order: the record outlives one registration's answer.
  const relationshipField = fields.relationship;
  const stored = relationshipField ? (answers[relationshipField.id] ?? '') : '';
  const fromRecord = match && person ? (person.relations[match.contactId] ?? null) : null;
  const relationshipId = fromRecord ?? relationshipFromOption(stored);
  const gender = match?.gender ?? null;
  const word = wordFor(relationshipId, gender);
  const wantsGender = Boolean(relationshipId) && needsGender(relationshipId) && !gender && !!match;
  const answered = Boolean(word) && !wantsGender;

  const pick = (who: { name: string; phone: string | null }) => {
    setAnswer(fields.name.id, who.name);
    if (fields.phone) setAnswer(fields.phone.id, who.phone ? prettyPhone(who.phone) : '');
    setEditing(false);
  };

  const choose = (choice: RelationChoice) => {
    if (relationshipField)
      setAnswer(
        relationshipField.id,
        formValueFor(choice.relationshipId, relationshipField.options)
      );
    setAsking(false);
    if (person && match) {
      onLearn?.({
        contactId: person.contactId,
        relatedContactId: match.contactId,
        relationshipId: choice.relationshipId,
        gender: choice.gender ?? gender,
      });
      setSaved(true);
      window.setTimeout(() => setSaved(false), 1800);
    }
  };

  /**
   * WHAT WE KNOW IS AN ANSWER, NOT JUST A SENTENCE.
   *
   * MP's relationship field is required, and reading "Colton is Rosie's
   * father" off the records and then leaving the field empty meant a screen
   * that looked finished with a dead Continue button under it. If the record
   * can answer it, it answers it.
   *
   * Gender does not gate this: MP's own list says "Parent", not "Father", so
   * the form is satisfied the moment the relationship is known. The mother-or-
   * father question is about the record, and a question about the record must
   * never hold up a registration.
   */
  useEffect(() => {
    if (!relationshipField || !relationshipId) return;
    if ((answers[relationshipField.id] ?? '').trim()) return;
    setAnswer(relationshipField.id, formValueFor(relationshipId, relationshipField.options));
  }, [relationshipField, relationshipId, answers, setAnswer]);

  const possessive = registrantName.endsWith('s') ? `${registrantName}'` : `${registrantName}'s`;
  // The sentence is about a person, so it uses their name. A typed-in name we
  // cannot resolve is read for a first name rather than sliced at the first
  // space: a row still carrying MP's "Wirgau, Sarah" would otherwise open the
  // question with "Wirgau, is Colton's…".
  const them = match ? match.firstName : firstNameOf(name);

  return (
    <section>
      <h3 className="kicker mb-2">Emergency contact</h3>
      {editing ? (
        <Editor
          others={others}
          onPick={pick}
          onCancel={() => setEditing(false)}
          startTyping={!others.length}
        />
      ) : name ? (
        <>
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="card lift flex w-full items-center gap-3 p-3.5 text-left"
          >
            <Avatar name={name} image={photo} size={40} />
            <span className="min-w-0 flex-1">
              <span className="text-navy block truncate text-sm font-bold">{name}</span>
              {phone && <span className="text-slate block truncate text-xs">{phone}</span>}
            </span>
            <Pencil className="text-slate h-4 w-4 shrink-0" />
          </button>
          {relationshipField && form && isVisible(form, relationshipField, answers) && (
            <div className="mt-3">
              {answered && !asking ? (
                <p className="text-slate flex items-center gap-1.5 text-sm">
                  <span>
                    {them} is {possessive} <b className="text-navy">{word}</b>.
                  </span>
                  <button
                    type="button"
                    onClick={() => setAsking(true)}
                    className="text-teal-deep text-xs font-semibold hover:underline"
                  >
                    Change
                  </button>
                  {saved && <Check className="text-teal h-3.5 w-3.5" />}
                </p>
              ) : (
                <>
                  <p className="text-navy mb-2 text-sm font-semibold">
                    {wantsGender && !asking ? (
                      <>
                        {them} is {possessive} {word}
                        {' — '}
                        which one?
                      </>
                    ) : (
                      <>
                        {them} is {possessive}…
                      </>
                    )}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {(wantsGender && !asking
                      ? genderChoices(relationshipId as number)
                      : // Where MP knows they are female, "Dad" is a wrong
                        // answer taking up a row. See `choicesForGender`.
                        choicesForGender(gender)
                    ).map((choice) => (
                      <button
                        key={choice.label}
                        type="button"
                        onClick={() => choose(choice)}
                        className="border-line text-navy hover:border-teal hover:text-teal-deep rounded-full border px-3 py-1.5 text-xs font-semibold transition hover:scale-105 active:scale-95"
                      >
                        {choice.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </>
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="border-line text-slate hover:text-navy hover:border-teal w-full rounded-2xl border border-dashed p-3.5 text-left text-sm font-semibold transition"
        >
          Add an emergency contact
        </button>
      )}
    </section>
  );
}

function Editor({
  others,
  onPick,
  onCancel,
  startTyping,
}: {
  others: ClientPerson[];
  onPick: (who: { name: string; phone: string | null }) => void;
  onCancel: () => void;
  startTyping: boolean;
}) {
  const [typing, setTyping] = useState(startTyping);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const ready = name.trim().length > 1 && phoneOk(phone);

  return (
    <div className="border-line bg-mist/40 rounded-2xl border p-3">
      {others.length > 0 && (
        <>
          <p className="kicker mb-1.5">In their household</p>
          <div className="grid gap-1">
            {others.map((p) => (
              <button
                key={p.contactId}
                type="button"
                onClick={() =>
                  onPick({ name: `${p.firstName} ${p.lastName}`.trim(), phone: p.phone })
                }
                className="hover:bg-paper flex w-full items-center gap-2.5 rounded-xl px-2 py-1.5 text-left transition"
              >
                <Avatar name={`${p.firstName} ${p.lastName}`} image={p.image} size={30} />
                <span className="min-w-0 flex-1">
                  <span className="text-navy block truncate text-sm font-semibold">
                    {p.firstName} {p.lastName}
                  </span>
                  {p.phone && (
                    <span className="text-slate block truncate text-xs">
                      {prettyPhone(p.phone)}
                    </span>
                  )}
                </span>
              </button>
            ))}
          </div>
        </>
      )}

      {typing ? (
        <div className="mt-2.5 grid gap-2 sm:grid-cols-2">
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Their name"
            className="field"
          />
          <input
            value={phone}
            onChange={(e) => setPhone(prettyPhone(e.target.value))}
            placeholder="Mobile"
            type="tel"
            inputMode="tel"
            className="field"
          />
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setTyping(true)}
          className="text-slate hover:text-navy mt-2 block text-sm font-semibold"
        >
          Someone else…
        </button>
      )}

      <div className="mt-3 flex items-center gap-3">
        {typing && (
          <button
            type="button"
            disabled={!ready}
            onClick={() => onPick({ name: name.trim(), phone })}
            className="btn btn-primary disabled:opacity-50"
          >
            Save
          </button>
        )}
        <button
          type="button"
          onClick={onCancel}
          className="text-slate text-xs font-semibold hover:underline"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

/**
 * A choice question an experience dressed: each answer a card with the
 * caterer's photo, its words and tags, and a radio dot (the women's
 * conference microsite's LunchPicker). The value written is MP's answer
 * string, unchanged; an answer the dress does not know shows as itself.
 */
function DressedChoice({
  label,
  required,
  choices,
  dress,
  value,
  onChoose,
}: {
  label: string;
  required?: boolean;
  choices: string[];
  dress: Record<string, { label: string; detail?: string; tags?: string[]; photo?: string }>;
  value: string;
  onChoose: (value: string) => void;
}) {
  return (
    <section>
      <h3 className="text-navy text-[0.92rem] font-bold">
        {label}
        {required && <span className="text-rose ml-1">*</span>}
      </h3>
      <div className="mt-2 flex flex-col gap-2" role="radiogroup" aria-label={label}>
        {choices.map((c) => {
          const d = dress[c];
          const on = value === c;
          return (
            <button
              key={c}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => onChoose(c)}
              className={`card lift flex w-full items-center gap-3.5 p-2.5 pr-4 text-left ${on ? '!ring-teal !ring-2' : ''}`}
            >
              {d?.photo ? (
                <img
                  src={d.photo}
                  alt=""
                  aria-hidden
                  className="h-14 w-14 shrink-0 rounded-xl object-cover"
                />
              ) : (
                <span aria-hidden className="bg-mist h-14 w-14 shrink-0 rounded-xl" />
              )}
              <span className="min-w-0 flex-1 leading-tight">
                <span className="text-navy block text-sm font-semibold">{d?.label ?? c}</span>
                {d?.detail || d?.tags?.length ? (
                  <span className="text-slate mt-0.5 block text-xs">
                    {[d?.detail, ...(d?.tags ?? [])].filter(Boolean).join(' · ')}
                  </span>
                ) : null}
              </span>
              <span
                aria-hidden
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${on ? 'border-teal' : 'border-line'}`}
              >
                {on ? <span className="bg-teal h-2.5 w-2.5 rounded-full" /> : null}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
